import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { auth, db } from '../../firebase';
import { signOut } from 'firebase/auth';
import {
  IconLayoutDashboard,
  IconBook2,
  IconPackage,
  IconInbox,
  IconBuildingFactory2,
  IconSettings,
  IconLogout,
  IconShieldCheck,
  IconSignature,
  IconBriefcase,
  IconFileStack,
  IconTemplate,
  IconBuilding,
} from '@tabler/icons-react';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const q = query(collection(db, 'libraryRequests'), where('status', '==', 'pending'));
    const unsub = onSnapshot(q, snap => setPendingCount(snap.size), () => setPendingCount(0));
    return unsub;
  }, []);

  // Unseen client activity on Needs you (replies, uploads, marked done).
  const [clientActivity, setClientActivity] = useState(0);
  useEffect(() => onSnapshot(query(collection(db, 'hqNotifications'), where('readAt', '==', null)),
    snap => setClientActivity(snap.size), () => setClientActivity(0)), []);

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const NAV_ITEMS = [
    { path: '/dashboard',        label: 'Dashboard',         icon: IconLayoutDashboard },
    { path: '/library',          label: 'Library',           icon: IconBook2, exact: true },
    { path: '/library/parts',    label: 'Parts',             icon: IconPackage, indent: true },
    { path: '/library/requests', label: 'Library requests',  icon: IconInbox, indent: true, badge: pendingCount },
    { path: '/customers',        label: 'Customers',         icon: IconBuildingFactory2 },
    { path: '/contracts',        label: 'Contracts',         icon: IconSignature },
    { path: '/packs',            label: 'Signing packs',     icon: IconFileStack, indent: true },
    { path: '/templates',        label: 'Templates',         icon: IconTemplate, indent: true },
    { path: '/company',          label: 'Company details',   icon: IconBuilding, indent: true },
    { path: '/projects',         label: 'Client projects',   icon: IconBriefcase, badge: clientActivity },
  ];

  const isActive = (item) => {
    if (item.path === '/dashboard') return location.pathname === '/' || location.pathname === '/dashboard';
    if (item.exact) {
      return location.pathname === item.path
        || (location.pathname.startsWith('/library/') && !location.pathname.startsWith('/library/parts') && !location.pathname.startsWith('/library/requests'));
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <div style={{
      width: 200, background: '#141B24',
      display: 'flex', flexDirection: 'column', flexShrink: 0,
      height: '100vh', position: 'sticky', top: 0,
    }}>
      <div style={{ padding: '16px 12px 14px', borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--color-brand)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconShieldCheck size={15} color="#fff" stroke={1.5} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 400, color: '#fff', lineHeight: 1.1 }}>Agriware HQ</div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.04em', marginTop: 2 }}>INTERNAL</div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px' }}>
        {NAV_ITEMS.map(item => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <div key={item.path} onClick={() => navigate(item.path)}
              style={{
                display: 'flex', alignItems: 'center', gap: 9,
                padding: '8px 10px', paddingLeft: item.indent ? 24 : 10,
                borderRadius: 6, fontSize: 13, cursor: 'pointer',
                color: active ? 'var(--color-brand)' : 'rgba(255,255,255,0.5)',
                background: active ? 'rgba(232,97,10,0.12)' : 'transparent',
                marginBottom: 2, transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.background = 'transparent'; } }}
            >
              <Icon size={item.indent ? 14 : 16} stroke={1.5} />
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.badge > 0 && (
                <span style={{
                  fontSize: 9, padding: '1px 6px', borderRadius: 'var(--radius-full)',
                  background: 'var(--color-brand)', color: '#fff',
                  minWidth: 14, textAlign: 'center',
                }}>
                  {item.badge}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ padding: 8, borderTop: '0.5px solid rgba(255,255,255,0.06)' }}>
        <div onClick={() => navigate('/settings')}
          style={{
            display: 'flex', alignItems: 'center', gap: 9,
            padding: '8px 10px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
            color: location.pathname.startsWith('/settings') ? 'var(--color-brand)' : 'rgba(255,255,255,0.5)',
            background: location.pathname.startsWith('/settings') ? 'rgba(232,97,10,0.12)' : 'transparent',
            marginBottom: 6, transition: 'all 0.15s',
          }}
          onMouseEnter={e => { if (!location.pathname.startsWith('/settings')) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          onMouseLeave={e => { if (!location.pathname.startsWith('/settings')) e.currentTarget.style.background = 'transparent'; }}
        >
          <IconSettings size={16} stroke={1.5} />
          Settings
        </div>

        <div style={{ padding: '6px 10px', fontSize: 11, color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {userProfile?.name || userProfile?.email || 'Loading...'}
        </div>

        <div onClick={handleSignOut}
          style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: 'rgba(255,255,255,0.35)', transition: 'color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.85)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}
        >
          <IconLogout size={14} stroke={1.5} />
          Sign out
        </div>
      </div>
    </div>
  );
}
